<script setup>
/* eslint semi: 1 */
import { computed, inject } from 'vue';
import CONST from '@/globals/constants';
import DurationInput from '@/components/common/duration-input/duration-input-tmpl';
import BezelsMask from '@/components/pages/create/bezels-mask/bezels-mask-tmpl.vue';

const $utils = inject('$utils');

const props = defineProps({
  item: {
    type: Object,
    default: () => ({}),
  },
  isAudioPicker: {
    type: Boolean,
    default: false,
  },
  // Whether this card is currently selected
  isSelected: {
    type: Boolean,
    default: false,
  },
  // Whether any audio item is currently playing
  isAudioPlaying: {
    type: Boolean,
    default: false,
  },
  // Whether THIS item's audio is the one currently playing
  isCurrentAudioPlaying: {
    type: Boolean,
    default: false,
  },
  // Orientation for SVG/HTML preview URL (horizontal/vertical)
  orientation: {
    type: String,
    default: '',
  },
  // Whether this preview is being rendered in a minified version
  isMini: {
    type: Boolean,
    default: false,
  },
  // Videowall bezel mask to overlay on the preview.
  // Shape: { bezels, resolutionWidth, resolutionHeight, cardWidth, cardHeight }
  bezelsMask: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['video-play', 'image-preview', 'audio-play', 'audio-pause', 'svg-load']);

// Image items include both plain image and logo types
const isItemImage = computed(() =>
  [CONST.MEDIA_TYPE_VALUE_IMAGE, CONST.MEDIA_TYPE_VALUE_LOGO].includes(props.item?.preview?.type)
);
// Include both svg and html project preview types
const isItemSVG = computed(() =>
  [CONST.MEDIA_PREVIEW_TYPE_VALUE_SVG, CONST.MEDIA_PREVIEW_TYPE_VALUE_HTML].includes(props.item?.preview?.type) &&
  ![CONST.MEDIA_TYPE_VALUE_AUDIO, CONST.MEDIA_TYPE_VALUE_AUDIO_PLAYLIST].includes(props.item?.type)
);
// Audio items include both plain audio and audio playlist types
const isItemAudio = computed(() =>
  [CONST.MEDIA_TYPE_VALUE_AUDIO, CONST.MEDIA_TYPE_VALUE_AUDIO_PLAYLIST].includes(props.item?.type ?? '')
);
// Video items
const isItemVideo = computed(() => props.item?.type === CONST.MEDIA_TYPE_VALUE_VIDEO);

// Show expand icon for image (img) and elementi types when item is fully ready
const showImageAction = computed(() =>
  props.item?.preview != null &&
  [CONST.MEDIA_TYPE_VALUE_IMAGE, CONST.MEDIA_TYPE_VALUE_ELEMENTI].includes(props.item?.type) &&
  !props.item?.status
);
// Show audio play/pause actions for plain audio items (not playlist) that are fully ready
const showAudioActions = computed(() =>
  props.item?.type === CONST.MEDIA_TYPE_VALUE_AUDIO &&
  !props.item?.status
);

// Preview URL for img tag — includes orientation query param when provided. Note that for SVG/HTML previews we use a separate computed property to include the orientation query param, as the implementation for SVG/HTML preview iframes requires the orientation to be included in the data-src attribute instead of the src attribute.
const previewURL = computed(() => props.item.preview !== null
  ? $utils.filterMediaPreviewUrl(props.item.preview.url)
  : ''
);
const trimmedPreviewURL = computed(() => $utils.trimDataSrc(props.item.preview?.url || ''));

// SVG/HTML preview URL — includes orientation query param when provided
const svgPreviewURL = computed(() => {
  if (!props.item?.preview?.url) return '';
  return $utils.filterMediaPreviewUrl(props.item.preview.url, {
    queryParams: props.orientation ? { dir: props.orientation } : {}
  });
});
const trimmedSvgPreviewURL = computed(() => {
  if (!props.item?.preview?.url) return '';
  return $utils.trimDataSrc(props.item.preview.url, {
    queryParams: props.orientation ? { dir: props.orientation } : {}
  });
});

// Video duration formatted as mm:ss for display on video items
const itemVideoDuration = computed(() => isItemVideo.value
  ? $utils.formatDurationToMinutesAndSeconds({ duration: props.item.dur })
  : ''
);

const hasNoPreview = computed(() => !props.item || props.item.preview === null || props.item.type === undefined);
const showAudioNoteButton = computed(() => props.item?.type === CONST.MEDIA_TYPE_VALUE_AUDIO && !props.item?.preview?.status);
const showAudioIframe = computed(() => props.item?.type === CONST.MEDIA_TYPE_VALUE_AUDIO_PLAYLIST || !!props.item?.preview?.status);
</script>

<template>
  <div class="relative w-full h-full overflow-hidden">
    <!-- Image / Logo / Video Preview -->
    <img v-if="isItemImage"
      :src="previewURL"
      class="object-cover object-center border-0 w-full h-full"
      loading="lazy"
    />

    <!-- Project / SVG / HTML Preview -->
    <iframe v-if="isItemSVG"
      :id="`mediaProjectFile_${item.resourceId}`"
      :src="svgPreviewURL"
      :data-src="trimmedSvgPreviewURL"
      :class="`media-preview-${item.resourceId} border-0 template-target-on-refresh-token media-project-file w-full h-full object-cover object-center`"
      loading="lazy"
      @load="emit('svg-load', { elId: `mediaProjectFile_${item.resourceId}` })"
    />

    <!-- Audio — single wrapper for all audio types. -->
    <div v-if="isItemAudio" class="absolute inset-0 w-full h-full">
      <div
        v-if="isAudioPicker"
        class="absolute top-0 left-0 bottom-0 bg-cover bg-center w-[76px] h-[42px] min-[1920px]:w-[126px] min-[1920px]:h-[70px]"
      >
        <!-- Status / playlist iframe -->
        <iframe
          v-if="showAudioIframe"
          :id="`mediaProjectFile_${item.resourceId}`"
          :src="previewURL"
          :data-src="trimmedPreviewURL"
          :class="`block border-0 template-target-on-refresh-token media-project-file w-full h-full z-[1] media-preview-${item.resourceId}`"
        />

        <!-- Note icon: plain audio, no status -->
        <el-button
          v-if="showAudioNoteButton"
          class="cstm-icon-btn-medium audio_note_white_bacgkround absolute inset-0 m-auto z-[2]"
        />
      </div>
      <template v-else>
        <!-- B1: Plain audio — clouds bg + note icon + title bar -->
        <template v-if="showAudioNoteButton">
          <div class="audio-details-background absolute inset-0 w-full h-full bg-cover bg-center -scale-x-100 z-0" />

          <div
            v-if="!isMini"
            class="absolute left-0 bottom-0 w-full box-border text-black uppercase bg-[#96ddf8] z-[1] text-xs px-[10px] pt-[8px] pb-[1px] h-[41px] min-[1920px]:px-[16px] min-[1920px]:pt-[10px] min-[1920px]:pb-[2px] min-[1920px]:h-[48px]"
          >
            <template v-if="item.metadata?.title || item.metadata?.artist">
              <div
                v-if="item.metadata?.title"
                class="w-full pb-1 whitespace-nowrap overflow-hidden text-ellipsis mt-[2px] min-[1920px]:mt-1"
              >
                {{ item.metadata.title }}
              </div>
              <div
                v-if="item.metadata?.artist"
                class="w-full pb-1 whitespace-nowrap overflow-hidden text-ellipsis"
              >
                {{ item.metadata.artist }}
              </div>
            </template>
            <template v-else>
              <div class="w-full pb-1 whitespace-nowrap overflow-hidden text-ellipsis mt-[2px] min-[1920px]:mt-1">
                {{ item.name }}
              </div>
            </template>
          </div>
        </template>

        <iframe
          v-if="showAudioIframe"
          :id="`mediaProjectFile_${item.resourceId}`"
          :src="previewURL"
          :data-src="trimmedPreviewURL"
          :class="`block border-0 template-target-on-refresh-token media-project-file w-full h-full media-preview-${item.resourceId}`"
        />
      </template>
    </div>

    <!-- Video Play Icon (hidden by default; parent reveals it on selection via [&_.video-play-icon]) -->
    <template v-if="isItemVideo">
      <div
        v-if="item.preview?.hasOwnProperty('type') && !item.status"
        class="video-play-icon"
        :class="{
          'inset-[calc(50%-24px)] w-12 h-12': !isMini,
          'inset-[calc(50%-14px)] w-7 h-7': isMini,
        }"
        @click.stop="emit('video-play', item.videopreviews)"
      >
        <font-awesome-icon icon="fa-regular fa-circle-play" class="w-full h-full" />
      </div>

      <span
        v-if="!isMini"
        class="absolute bottom-[10px] right-[10px] text-xs"
      >
        <duration-input
          v-if="item.preview?.hasOwnProperty('type') && item.type === 'video' && !item.status"
          :model-value="itemVideoDuration"
          readonly
        />
      </span>
    </template>

    <!-- Image / Elementi Expand Icon (hidden by default; parent reveals it on selection via [&_.image-preview-icon]) -->
    <template v-if="showImageAction">
      <div
        class="image-preview-icon"
        :class="{
          'inset-[calc(50%-24px)] w-12 h-12': !isMini,
          'inset-[calc(50%-14px)] w-7 h-7': isMini,
        }"
        @click.stop="emit('image-preview', item.preview)"
      >
        <font-awesome-icon icon="fa-solid fa-expand" class="w-full h-full" />
      </div>
    </template>

    <!-- Audio Play/Pause Actions (grid mode only — not audio-picker, plain audio, item fully ready) -->
    <div
      v-if="showAudioActions && !isAudioPicker"
      class="audio-preview-actions absolute left-0 right-0 mx-auto z-[2]"
      :class="{
        'w-16 h-auto top-[calc(50%-32px)]': !isMini,
        'w-10 h-10 top-[calc(50%-20px)]': isMini,
      }"
    >
      <!-- Selected state: play button (white bg) or pause -->
      <template v-if="isSelected">
        <el-button
          v-if="!isCurrentAudioPlaying"
          @click.stop="emit('audio-play', item)"
          class="cstm-icon-btn-large play_white_bacgkround"
        />
        <el-button
          v-if="isCurrentAudioPlaying"
          @click.stop="emit('audio-pause', item)"
          class="cstm-icon-btn-large pause_white_bacgkround"
        />
      </template>
      <!-- Unselected state: music note button or pause -->
      <template v-else>
        <el-button
          v-if="!isCurrentAudioPlaying"
          @click.stop="emit('audio-play', item)"
          class="cstm-icon-btn-large audio_note_white_bacgkround"
        />
        <el-button
          v-if="isCurrentAudioPlaying"
          @click.stop="emit('audio-pause', item)"
          class="cstm-icon-btn-large pause_white_bacgkround"
        />
      </template>
    </div>

    <!-- Videowall Bezel Mask -->
    <BezelsMask
      v-if="bezelsMask"
      :bezels="bezelsMask.bezels"
      :resolutionWidth="bezelsMask.resolutionWidth"
      :resolutionHeight="bezelsMask.resolutionHeight"
      :cardWidth="bezelsMask.cardWidth"
      :cardHeight="bezelsMask.cardHeight"
      class="absolute top-0 left-1/2 translate-x-[-50%]"
    />

    <!-- No Preview Fallback -->
    <div
      v-if="hasNoPreview"
      class="w-full h-full flex items-center justify-center box-border bg-[#e7e7e7] border border-[#c2c2c2] text-[#747474]"
    >
      <div class="flex flex-col items-center gap-1 text-center">
        <el-icon class="el-icon-warning"><WarningFilled /></el-icon>
        <p class="m-0 text-xs leading-tight">{{ $t('label.previewUnavailable') }}</p>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '../../../static/style/mixins.scss' as *;
@reference '#tailwind.scss';

.audio-details-background {
  background-image: url('../../../static/images/clouds-02.jpg');
}

.play_white_bacgkround, .pause_white_bacgkround, .audio_note_white_bacgkround {
  @apply w-10! h-10!;
  &:after {
    @apply w-10! h-10! bg-size-[85%]! top-0! left-0!;
    background-size: 85% !important;
  }
}

.audio_note_white_bacgkround {
  &:hover {
    &:after {
      @apply bg-size-[85%]! top-0!;
      background: url('../../../static/icons/play_plain_black.svg') no-repeat center center !important;
      background-size: 85% !important;
    }
  }
}

.video-play-icon, .image-preview-icon {
  @apply absolute block opacity-50 hover:opacity-100 cursor-pointer z-[2];
}
</style>
